import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'wouter';
import { api, describeApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';
import { QASkeleton } from '../components/QASkeleton';
import { formatAUD, formatDateAU } from '../../../shared/format';
import {
  OPEN_STAGES, PIPELINE_STAGES, PROSPECT_SOURCES, SOURCE_LABEL, STAGE_LABEL,
  type PipelineStage, type ProspectSource, type SourceConversion, type StageColumn,
} from '../../../shared/pipeline';

interface Prospect {
  id: string;
  firstName: string;
  surname: string;
  partnerName: string | null;
  email: string | null;
  phone: string | null;
  source: ProspectSource | null;
  referredBy: string | null;
  stage: PipelineStage;
  estimatedFumCents: number | null;
  nextStep: string | null;
  nextStepDue: string | null;
  notes: string | null;
  lostReason: string | null;
  convertedClientId: string | null;
  convertedAt: string | null;
  ownerName: string | null;
  daysInStage: number;
}
interface PipelineResponse {
  data: Prospect[];
  columns: StageColumn[];
  conversion: { rows: SourceConversion[]; totals: SourceConversion };
  today: string;
}

type Draft = {
  firstName: string; surname: string; partnerName: string; email: string; phone: string; source: string; referredBy: string;
  stage: PipelineStage; estimatedFum: string; nextStep: string; nextStepDue: string; notes: string; lostReason: string;
};
const EMPTY: Draft = {
  firstName: '', surname: '', partnerName: '', email: '', phone: '', source: '', referredBy: '', stage: 'enquiry',
  estimatedFum: '', nextStep: '', nextStepDue: '', notes: '', lostReason: '',
};
const toCents = (s: string) => (s.trim() === '' ? null : Math.round(Number(s.replace(/[$,\s]/g, '')) * 100));
const fromCents = (c: number | null) => (c == null ? '' : String(c / 100));
const pct = (n: number | null) => (n == null ? '-' : `${Math.round(n * 100)}%`);
const shortAUD = (cents: number) => {
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}M`;
  if (d >= 1_000) return `$${Math.round(d / 1_000)}K`;
  return formatAUD(d);
};

/** Australian financial year start for a Brisbane date (1 July). */
function fyStart(today: string): string {
  const y = Number(today.slice(0, 4));
  return Number(today.slice(5, 7)) >= 7 ? `${y}-07-01` : `${y - 1}-07-01`;
}

/**
 * Prospects and pipeline: leads move enquiry -> first meeting -> fact find -> SoA,
 * then Convert makes them a client (or links an existing one). Lost leads keep their
 * reason. The report shows which sources convert.
 */
export function Pipeline() {
  const { user } = useAuth();
  const canEdit = can(user?.role, 'client.edit');
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [window_, setWindow] = useState<'all' | 'fy' | '90'>('all');
  const q = useQuery({ queryKey: ['prospects'], queryFn: () => api<PipelineResponse>('/api/prospects') });
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);

  const rows = useMemo(() => q.data?.data ?? [], [q.data]);
  const today = q.data?.today ?? '';

  // ?prospect=<id> (from Today) opens that prospect.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('prospect');
    if (!id || !rows.length || editing) return;
    const p = rows.find((r) => r.id === id);
    if (p) setEditing({ id: p.id, draft: toDraft(p) });
  }, [rows, editing]);

  const since = window_ === 'all' || !today ? undefined
    : window_ === 'fy' ? fyStart(today)
      : new Date(Date.parse(`${today}T00:00:00Z`) - 90 * 86_400_000).toISOString().slice(0, 10);
  const report = useQuery({
    queryKey: ['prospects', 'report', since ?? 'all'],
    queryFn: () => api<PipelineResponse>(`/api/prospects${since ? `?since=${since}` : ''}`),
    enabled: !!q.data,
  });

  const byStage = useMemo(() => {
    const m = new Map<PipelineStage, Prospect[]>();
    for (const s of PIPELINE_STAGES) m.set(s, []);
    for (const r of rows) m.get(r.stage)?.push(r);
    for (const list of m.values()) list.sort((a, b) => (a.nextStepDue ?? '9999').localeCompare(b.nextStepDue ?? '9999') || a.surname.localeCompare(b.surname));
    return m;
  }, [rows]);

  if (q.isLoading) return <QASkeleton variant="dashboard" />;
  if (q.isError || !q.data) return <div className="qa-badge qa-badge--out">Unable to load the pipeline</div>;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['prospects'] });
    void qc.invalidateQueries({ queryKey: ['today'] });
  };
  const set = (patch: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e));
  const close = () => { setEditing(null); if (window.location.search) navigate('/pipeline', { replace: true }); };

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg(done); refresh(); return true; } catch (e) {
      setMsg(describeApiError(e)); return false;
    } finally { setBusy(false); }
  }

  async function save() {
    if (!editing) return;
    const d = editing.draft;
    const body = {
      firstName: d.firstName, surname: d.surname, partnerName: d.partnerName, email: d.email, phone: d.phone,
      source: d.source || null, referredBy: d.referredBy, stage: d.stage, estimatedFumCents: toCents(d.estimatedFum),
      nextStep: d.nextStep, nextStepDue: d.nextStepDue || null, notes: d.notes, lostReason: d.stage === 'lost' ? d.lostReason : null,
    };
    const ok = await run(() => api(editing.id ? `/api/prospects/${editing.id}` : '/api/prospects', {
      method: editing.id ? 'PATCH' : 'POST', body: JSON.stringify(body),
    }), editing.id ? `Saved ${d.firstName} ${d.surname}.` : `Added ${d.firstName} ${d.surname}.`);
    if (ok) close();
  }

  async function moveStage(p: Prospect, stage: PipelineStage) {
    await run(() => api(`/api/prospects/${p.id}`, { method: 'PATCH', body: JSON.stringify({ stage }) }),
      `${p.firstName} ${p.surname} moved to ${STAGE_LABEL[stage]}.`);
  }

  async function convert(p: Prospect) {
    if (!window.confirm(`Make ${p.firstName} ${p.surname} a client? A client record is created from the prospect.`)) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ data: { clientId: string } }>(`/api/prospects/${p.id}/convert`, { method: 'POST', body: '{}' });
      refresh();
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/clients/${r.data.clientId}`);
    } catch (e) {
      setMsg(describeApiError(e, 'Could not convert'));
    } finally { setBusy(false); }
  }

  async function remove(p: Prospect) {
    if (!window.confirm(`Remove ${p.firstName} ${p.surname} from the pipeline?`)) return;
    const ok = await run(() => api(`/api/prospects/${p.id}`, { method: 'DELETE' }), `Removed ${p.firstName} ${p.surname}.`);
    if (ok) close();
  }

  const open = q.data.columns.filter((c) => OPEN_STAGES.includes(c.stage));
  const openTotal = open.reduce((n, c) => n + c.count, 0);
  const openFum = open.reduce((n, c) => n + c.estimatedFumCents, 0);
  const closed = [...(byStage.get('implemented') ?? []), ...(byStage.get('lost') ?? [])];
  const conv = report.data?.conversion ?? q.data.conversion;

  return (
    <div>
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4 qa-rise qa-stagger-1">
        <div>
          <p className="qa-eyebrow mb-2">Prospects · {openTotal} open · {shortAUD(openFum)} estimated</p>
          <h1 className="qa-h1">The <span className="qa-italic-accent">pipeline.</span></h1>
          <p className="qa-deck mt-2 text-ink-soft">Enquiry to implemented. Convert makes the prospect a client.</p>
        </div>
        {canEdit && (
          <button type="button" className="btn-primary text-sm self-start md:self-end" onClick={() => setEditing({ id: null, draft: { ...EMPTY } })}>
            Add prospect
          </button>
        )}
      </div>

      <div role="status" aria-live="polite" className={msg ? 'qa-attention-banner mb-4 text-sm' : 'sr-only'}>{msg}</div>

      {editing && (
        <section className="qa-card p-5 mb-6" aria-label={editing.id ? 'Edit prospect' : 'New prospect'}>
          <p className="qa-eyebrow mb-4">{editing.id ? 'Edit prospect' : 'New prospect'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            <label>First name<input className="qa-input block w-full mt-1 text-sm" aria-label="First name" value={editing.draft.firstName} onChange={(e) => set({ firstName: e.target.value })} /></label>
            <label>Surname<input className="qa-input block w-full mt-1 text-sm" aria-label="Surname" value={editing.draft.surname} onChange={(e) => set({ surname: e.target.value })} /></label>
            <label>Partner<input className="qa-input block w-full mt-1 text-sm" aria-label="Partner name" value={editing.draft.partnerName} onChange={(e) => set({ partnerName: e.target.value })} /></label>
            <label>Stage
              <select className="qa-input block w-full mt-1 text-sm" aria-label="Stage" value={editing.draft.stage} onChange={(e) => set({ stage: e.target.value as PipelineStage })}>
                {[...OPEN_STAGES, 'lost' as const].map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
              </select>
            </label>
            <label>Email<input type="email" className="qa-input block w-full mt-1 text-sm" aria-label="Email" value={editing.draft.email} onChange={(e) => set({ email: e.target.value })} /></label>
            <label>Phone<input className="qa-input block w-full mt-1 text-sm" aria-label="Phone" value={editing.draft.phone} onChange={(e) => set({ phone: e.target.value })} /></label>
            <label>Source
              <select className="qa-input block w-full mt-1 text-sm" aria-label="Source" value={editing.draft.source} onChange={(e) => set({ source: e.target.value })}>
                <option value="">Not recorded</option>
                {PROSPECT_SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
              </select>
            </label>
            <label>Referred by<input className="qa-input block w-full mt-1 text-sm" aria-label="Referred by" value={editing.draft.referredBy} onChange={(e) => set({ referredBy: e.target.value })} /></label>
            <label>Estimated FUM ($)<input className="qa-input block w-full mt-1 text-sm qa-mono" inputMode="decimal" aria-label="Estimated FUM" value={editing.draft.estimatedFum} onChange={(e) => set({ estimatedFum: e.target.value })} /></label>
            <label className="lg:col-span-2">Next step<input className="qa-input block w-full mt-1 text-sm" aria-label="Next step" value={editing.draft.nextStep} onChange={(e) => set({ nextStep: e.target.value })} /></label>
            <label>Next step due<input type="date" className="qa-input block w-full mt-1 text-sm" aria-label="Next step due" value={editing.draft.nextStepDue} onChange={(e) => set({ nextStepDue: e.target.value })} /></label>
            {editing.draft.stage === 'lost' && (
              <label className="sm:col-span-2 lg:col-span-4">Why lost<input className="qa-input block w-full mt-1 text-sm" aria-label="Lost reason" value={editing.draft.lostReason} onChange={(e) => set({ lostReason: e.target.value })} /></label>
            )}
            <label className="sm:col-span-2 lg:col-span-4">Notes<textarea className="qa-input block w-full mt-1 text-sm" rows={3} aria-label="Notes" value={editing.draft.notes} onChange={(e) => set({ notes: e.target.value })} /></label>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {canEdit && <button type="button" className="btn-primary text-sm" disabled={busy || !editing.draft.firstName.trim() || !editing.draft.surname.trim()} onClick={save}>Save prospect</button>}
            {canEdit && editing.id && editing.draft.stage !== 'lost' && (() => {
              const p = rows.find((r) => r.id === editing.id);
              return p ? <button type="button" className="btn-ghost text-sm" disabled={busy} onClick={() => convert(p)}>Convert to client</button> : null;
            })()}
            {canEdit && editing.id && (() => {
              const p = rows.find((r) => r.id === editing.id);
              return p ? <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={() => remove(p)}>Remove</button> : null;
            })()}
            <button type="button" className="btn-ghost text-xs" onClick={close}>Cancel</button>
          </div>
        </section>
      )}

      <div className="flex gap-4 overflow-x-auto pb-2 qa-rise qa-stagger-2" data-testid="pipeline-board" role="region" aria-label="Pipeline stages" tabIndex={0}>
        {open.map((col) => (
          <section key={col.stage} className="qa-card p-4 flex-1 min-w-[240px]" aria-labelledby={`stage-${col.stage}`} data-testid={`stage-${col.stage}`}>
            <div className="flex items-baseline justify-between mb-3">
              <h2 id={`stage-${col.stage}`} className="qa-eyebrow">{col.label}</h2>
              <span className="qa-mono text-xs text-ink-mute">{col.count}{col.estimatedFumCents ? ` · ${shortAUD(col.estimatedFumCents)}` : ''}</span>
            </div>
            {(byStage.get(col.stage) ?? []).length === 0 ? (
              <p className="text-xs text-ink-mute py-2">None at this stage.</p>
            ) : (
              <ul className="space-y-2">
                {(byStage.get(col.stage) ?? []).map((p) => {
                  const overdue = !!p.nextStepDue && p.nextStepDue <= today;
                  const idx = OPEN_STAGES.indexOf(p.stage);
                  const next = OPEN_STAGES[idx + 1];
                  return (
                    <li key={p.id} className="border border-hairline-paper p-3 bg-paper" data-testid="prospect-card">
                      <button type="button" className="text-sm font-medium text-ink hover:text-rust text-left w-full" onClick={() => setEditing({ id: p.id, draft: toDraft(p) })}>
                        {p.surname}, {p.firstName}{p.partnerName ? ` & ${p.partnerName.split(/\s+/)[0]}` : ''}
                      </button>
                      <div className="text-xs text-ink-mute mt-1">
                        {p.source ? SOURCE_LABEL[p.source] : 'Source not recorded'}
                        {p.estimatedFumCents ? <span className="qa-mono"> · {shortAUD(p.estimatedFumCents)}</span> : null}
                        <span className="qa-mono"> · {p.daysInStage}d</span>
                      </div>
                      {(p.nextStep || p.nextStepDue) && (
                        <div className={`text-xs mt-1 ${overdue ? 'text-rust font-medium' : 'text-ink-soft'}`}>
                          {p.nextStep || 'Next step'}{p.nextStepDue ? ` · ${formatDateAU(p.nextStepDue)}` : ''}
                        </div>
                      )}
                      {canEdit && (
                        <div className="flex gap-3 mt-2">
                          {next && <button type="button" className="text-xs text-rust hover:underline" disabled={busy} aria-label={`Move ${p.firstName} ${p.surname} to ${STAGE_LABEL[next]}`} onClick={() => moveStage(p, next)}>To {STAGE_LABEL[next]}</button>}
                          {p.stage === 'soa' && <button type="button" className="text-xs text-rust hover:underline" disabled={busy} aria-label={`Convert ${p.firstName} ${p.surname} to a client`} onClick={() => convert(p)}>Convert</button>}
                          <button type="button" className="text-xs text-ink-mute hover:underline" disabled={busy} aria-label={`Mark ${p.firstName} ${p.surname} lost`}
                            onClick={() => setEditing({ id: p.id, draft: { ...toDraft(p), stage: 'lost' } })}>Lost</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ))}
      </div>

      <div className="mt-4">
        <button type="button" className="btn-ghost text-xs" aria-expanded={showClosed} onClick={() => setShowClosed((s) => !s)}>
          {showClosed ? 'Hide closed' : `Closed (${closed.length})`}
        </button>
        {showClosed && (
          closed.length === 0 ? <p className="text-sm text-ink-mute mt-2">No closed prospects yet.</p> : (
            <ul className="text-sm divide-y divide-hairline mt-2" data-testid="pipeline-closed">
              {closed.map((p) => (
                <li key={p.id} className="py-2 flex flex-wrap items-center gap-3">
                  <span className="font-medium">{p.surname}, {p.firstName}</span>
                  <span className={`qa-badge ${p.stage === 'implemented' ? 'qa-badge--pass' : 'qa-badge--mute'}`}>{STAGE_LABEL[p.stage]}</span>
                  {p.stage === 'implemented' && p.convertedClientId && <Link to={`/clients/${p.convertedClientId}`} className="text-xs text-rust hover:underline">Open client</Link>}
                  {p.stage === 'lost' && <span className="text-xs text-ink-mute">{p.lostReason || 'No reason recorded'}</span>}
                  {p.stage === 'lost' && canEdit && (
                    <button type="button" className="text-xs text-rust hover:underline" disabled={busy} onClick={() => moveStage(p, 'enquiry')}>Reopen</button>
                  )}
                </li>
              ))}
            </ul>
          )
        )}
      </div>

      <section className="mt-8" aria-labelledby="pipeline-report">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
          <h2 id="pipeline-report" className="qa-h3">Conversion by <span className="qa-italic-accent">source.</span></h2>
          <div className="qa-tabs" role="tablist" aria-label="Report window">
            {([['all', 'All time'], ['fy', 'This FY'], ['90', 'Last 90 days']] as const).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={window_ === k} className={`qa-tab ${window_ === k ? 'qa-tab--active' : ''}`} onClick={() => setWindow(k)}>{label}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto" role="region" aria-label="Conversion report" tabIndex={0}>
          <table className="qa-table w-full" data-testid="pipeline-report-table">
            <thead><tr><th>Source</th><th className="text-right">Prospects</th><th className="text-right">Open</th><th className="text-right">Won</th><th className="text-right">Lost</th><th className="text-right">Conversion</th><th className="text-right">Days to convert</th><th className="text-right">Won FUM</th></tr></thead>
            <tbody>
              {conv.rows.length === 0 ? (
                <tr><td colSpan={8} className="text-ink-mute text-sm">No prospects in this window.</td></tr>
              ) : conv.rows.map((r) => (
                <tr key={r.source}>
                  <td>{r.label}</td>
                  <td className="num">{r.total}</td><td className="num">{r.open}</td><td className="num">{r.converted}</td><td className="num">{r.lost}</td>
                  <td className="num">{pct(r.conversionRate)}</td>
                  <td className="num">{r.avgDaysToConvert ?? '-'}</td>
                  <td className="num">{r.convertedFumCents ? shortAUD(r.convertedFumCents) : '-'}</td>
                </tr>
              ))}
            </tbody>
            {conv.rows.length > 0 && (
              <tfoot><tr className="font-medium">
                <td>All sources</td>
                <td className="num">{conv.totals.total}</td><td className="num">{conv.totals.open}</td><td className="num">{conv.totals.converted}</td><td className="num">{conv.totals.lost}</td>
                <td className="num">{pct(conv.totals.conversionRate)}</td>
                <td className="num">{conv.totals.avgDaysToConvert ?? '-'}</td>
                <td className="num">{conv.totals.convertedFumCents ? shortAUD(conv.totals.convertedFumCents) : '-'}</td>
              </tr></tfoot>
            )}
          </table>
        </div>
        <p className="text-xs text-ink-mute mt-2">Conversion counts closed prospects only: won over won plus lost.</p>
      </section>
    </div>
  );
}

function toDraft(p: Prospect): Draft {
  return {
    firstName: p.firstName, surname: p.surname, partnerName: p.partnerName ?? '', email: p.email ?? '', phone: p.phone ?? '',
    source: p.source ?? '', referredBy: p.referredBy ?? '', stage: p.stage, estimatedFum: fromCents(p.estimatedFumCents),
    nextStep: p.nextStep ?? '', nextStepDue: p.nextStepDue ?? '', notes: p.notes ?? '', lostReason: p.lostReason ?? '',
  };
}
